// src/crm/tasks/useTasks.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Task, TaskStatus } from '../../lib/types'

export type CreateTaskInput = {
  appId: string
  dealId?: string | null
  creatorId?: string | null
  title: string
  dueDate?: string | null
  status?: TaskStatus
}

export type PatchTaskInput = {
  title?: string
  dueDate?: string | null
  status?: TaskStatus
}

export function useTasks(appId: string | null) {
  return useQuery<Task[]>({
    queryKey: ['tasks', appId],
    queryFn: () =>
      api
        .get<{ tasks: Task[] }>(`/api/tasks?appId=${appId ?? ''}`)
        .then((r) => r.tasks),
    enabled: appId !== null,
  })
}

export function useCreateTask(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTaskInput) =>
      api.post<{ task: Task }>('/api/tasks', data).then((r) => r.task),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks', appId] })
    },
  })
}

export function usePatchTask(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PatchTaskInput }) =>
      api.patch<{ task: Task }>(`/api/tasks/${id}`, data).then((r) => r.task),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks', appId] })
    },
  })
}

export function useDeleteTask(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/api/tasks/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks', appId] })
    },
  })
}
